import { AxiosError } from 'axios';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import { useBranchContext } from 'providers/BranchProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { TPurchaseOrder, TPurchaseOrderItem } from 'utils/types';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import useFetchSupplier from '../SupplierPayment/useFetchSupplier';
import { formatLongIsoDate } from 'utils/formatLongIsoDate';
import { formatNumberWithCommas } from 'utils/formats';
import BadgeStatus from 'utils/BadgeStatus';
import ProgressBadge from 'components/common/ProgressBadge';
import EstimatedUnitPriceCell, { formatPeso } from './EstimatedUnitPriceCell';
import ProductMovementBadge from 'components/shared/common/ProductMovementBadge';

type QuickCreateResult = {
  purchaseOrderId: number;
  purchaseOrderItemId?: number;
  supplierId?: number | null;
  supplierName?: string | null;
  wasPending: boolean;
  resolutionReason: string;
  productMatchQueued?: boolean;
};

const PendingPurchaseOrder = () => {
  const { selectedBranch } = useBranchContext();
  const { suppliers } = useFetchSupplier();
  const [pendingPo, setPendingPo] = useState<TPurchaseOrder | null>(null);
  const [items, setItems] = useState<TPurchaseOrderItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [remarks, setRemarks] = useState('');
  const [allowRecentlyOrdered, setAllowRecentlyOrdered] = useState(false);
  const [allowNonCriticalQuantity, setAllowNonCriticalQuantity] =
    useState(false);
  const [confirmMessage, setConfirmMessage] = useState<string | null>(null);
  const assignItemIdRef = useRef<number | null>(null);
  const assignSupplierIdRef = useRef<number | null>(null);
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });

  const loadPending = useCallback(async () => {
    if (!selectedBranch?.branchDomain) return;
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.get<TPurchaseOrder>(
        `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/pending`
      );
      setPendingPo(data);

      const itemsRes = await axiosInstance.get(
        `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/${data.purchaseOrderId}/items`,
        { params: { page: 1, pageSize: 200 } }
      );
      setItems(itemsRes.data.data ?? itemsRes.data.Data ?? []);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to load pending list',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedBranch?.branchDomain]);

  useEffect(() => {
    void loadPending();
  }, [loadPending]);

  const handleAddByCode = async () => {
    if (!selectedBranch?.branchDomain) return;
    if (!code.trim()) {
      toast.error('Enter a product code', { ...toastConfig, autoClose: 3000 });
      return;
    }
    if (!quantity || quantity < 1) {
      toast.error('Quantity must be at least 1', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    try {
      const { data } = await axiosInstance.post<QuickCreateResult>(
        `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/pending/items`,
        {
          code: code.trim(),
          quantity,
          unitOfMeasure: 'Piece',
          remarks: remarks || null,
          allowRecentlyOrdered,
          allowNonCriticalQuantity
        }
      );

      if (data.wasPending) {
        toast.success(
          data.productMatchQueued
            ? 'Item added to pending list. Matching product…'
            : 'Item added to pending list',
          { ...toastConfig, autoClose: 3000 }
        );
      } else {
        toast.success(
          <span>
            Resolved to {data.supplierName || 'supplier'}.{' '}
            <Link to={`/purchase-order-details/${data.purchaseOrderId}`}>
              View PO
            </Link>
          </span>,
          { ...toastConfig, autoClose: 4000 }
        );
      }

      setCode('');
      setQuantity(1);
      setRemarks('');
      setConfirmMessage(null);
      setAllowRecentlyOrdered(false);
      setAllowNonCriticalQuantity(false);
      await loadPending();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400) {
        const message =
          error.response.data?.errorMessage ||
          error.response.data?.message ||
          'Unable to add item';

        if (message.includes('Product still has')) {
          setConfirmMessage(message);
          setAllowNonCriticalQuantity(true);
          return;
        }
        if (message.includes('ordered recently')) {
          setConfirmMessage(message);
          setAllowRecentlyOrdered(true);
          return;
        }
        toast.error(message, { ...toastConfig, autoClose: 3000 });
        return;
      }
      toast.error('Unable to add item', { ...toastConfig, autoClose: 3000 });
    }
  };

  const handleAssignSupplier = async () => {
    const itemId = assignItemIdRef.current;
    const supplierId = assignSupplierIdRef.current;
    if (!selectedBranch?.branchDomain || !itemId || !supplierId) {
      toast.error('Select a supplier', { ...toastConfig, autoClose: 3000 });
      return;
    }

    try {
      const { data } = await axiosInstance.put<QuickCreateResult>(
        `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/pending/items/${itemId}/assignSupplier`,
        { supplierId }
      );
      toast.success(
        <span>
          Assigned to {data.supplierName || 'supplier'}.{' '}
          <Link to={`/purchase-order-details/${data.purchaseOrderId}`}>
            View PO
          </Link>
        </span>,
        { ...toastConfig, autoClose: 4000 }
      );
      modalRef.current.hide();
      await loadPending();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to assign supplier',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    }
  };

  const openAssignModal = (itemId: number) => {
    assignItemIdRef.current = itemId;
    assignSupplierIdRef.current = null;
    setModalProps({
      title: 'Assign supplier',
      content: (
        <Form.Group>
          <Form.Label>Supplier</Form.Label>
          <Form.Select
            defaultValue=""
            onChange={e => {
              assignSupplierIdRef.current = e.target.value
                ? Number(e.target.value)
                : null;
            }}
          >
            <option value="">Select supplier</option>
            {suppliers?.map(s => (
              <option key={s.supplierId} value={s.supplierId}>
                {s.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => void handleAssignSupplier()
    });
    modalRef.current.show();
  };

  return (
    <>
      <Row className="mb-3 align-items-center justify-content-between">
        <Col>
          <h4 className="mb-1">Pending purchase order list</h4>
          <p className="text-body-tertiary mb-0">
            Items waiting for a supplier. Add by code or assign a supplier
            manually.
          </p>
        </Col>
        <Col xs="auto">
          <Link to="/purchase-order" className="btn btn-phoenix-secondary">
            Back to purchase orders
          </Link>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Add item by code</h5>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="OEM / barcode / part code"
              />
            </Col>
            <Col md={2}>
              <Form.Label>Qty</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Button className="w-100" onClick={() => void handleAddByCode()}>
                Add
              </Button>
            </Col>
          </Row>
          {confirmMessage && (
            <p className="text-warning mt-3 mb-0">
              {confirmMessage} Click Add again to continue.
            </p>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex align-items-center gap-2 mb-3">
            <h5 className="mb-0">
              Pending PO #{pendingPo?.purchaseOrderId ?? '—'}
            </h5>
            {pendingPo && <BadgeStatus status={pendingPo.status} />}
            {pendingPo?.hasBackgroundTaskInProgress && (
              <ProgressBadge label="Matching…" title="Product match in progress" />
            )}
            {pendingPo && (
              <span className="ms-auto fs-9">
                Est. amount: ₱
                {formatNumberWithCommas(pendingPo.estimatedProcurementAmount)}
                {pendingPo.hasIncompleteEstimate && (
                  <span className="text-body-tertiary ms-1">partial</span>
                )}
              </span>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Est. Unit Price</th>
                  <th>Est. Amount</th>
                  <th>Match</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-body-tertiary">
                      No pending items
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <tr key={item.purchaseOrderItemId}>
                    <td>
                      <span className="d-inline-flex align-items-center gap-2 flex-wrap">
                        <span>{item.productName || '—'}</span>
                        <ProductMovementBadge
                          movementClass={item.movementClass}
                          netQuantitySoldLast90Days={
                            item.netQuantitySoldLast90Days
                          }
                          distinctSaleDaysLast90Days={
                            item.distinctSaleDaysLast90Days
                          }
                          lastSoldOn={item.lastSoldOn}
                          firstDeliveredOn={item.firstDeliveredOn}
                          tooltipId={`pending-po-movement-${item.purchaseOrderItemId}`}
                        />
                      </span>
                      {!item.productId && (
                        <div className="fs-9 text-body-tertiary">
                          Unmatched code
                        </div>
                      )}
                    </td>
                    <td>{item.quantity}</td>
                    <td>
                      <EstimatedUnitPriceCell
                        item={item}
                        branchDomain={selectedBranch?.branchDomain}
                        purchaseOrderId={
                          pendingPo?.purchaseOrderId != null
                            ? String(pendingPo.purchaseOrderId)
                            : undefined
                        }
                        canEdit={pendingPo?.status !== 'Closed'}
                        onUpdated={updated => {
                          setItems(prev => {
                            const next = prev.map(row =>
                              row.purchaseOrderItemId ===
                              updated.purchaseOrderItemId
                                ? { ...row, ...updated }
                                : row
                            );
                            setPendingPo(po =>
                              po
                                ? {
                                    ...po,
                                    estimatedProcurementAmount: next.reduce(
                                      (sum, row) =>
                                        sum + (row.estimatedAmount ?? 0),
                                      0
                                    ),
                                    hasIncompleteEstimate: next.some(
                                      row => row.estimatedAmount == null
                                    )
                                  }
                                : po
                            );
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td>{formatPeso(item.estimatedAmount)}</td>
                    <td>
                      {item.productMatchStatus === 'Queued' ||
                      item.productMatchStatus === 10
                        ? 'Queued'
                        : item.suggestionStatus === 'Pending'
                          ? 'Suggestion'
                          : item.productId
                            ? 'Linked'
                            : '—'}
                    </td>
                    <td>
                      {item.orderByUserName || 'System'}
                      <div className="fs-9 text-body-tertiary">
                        {/* createdOn may not be on DTO; show PO created as fallback */}
                        {pendingPo?.createdOn
                          ? formatLongIsoDate(pendingPo.createdOn)
                          : ''}
                      </div>
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="phoenix-primary"
                        onClick={() => openAssignModal(item.purchaseOrderItemId)}
                      >
                        Assign supplier
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
      />
    </>
  );
};

export default PendingPurchaseOrder;
